import React from 'react'
import { Eye, RefreshCcw, Trash2 } from 'lucide-react'

import type { RequestItem } from '../../console-types'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import { formatTime, messageKindBadgeVariant } from './shared'

export function RequestsTab({
  requests,
  requestsTotal,
  isLoadingRequests,
  onRefresh,
  onClear,
  onOpenDetail,
}: {
  requests: RequestItem[]
  requestsTotal: number
  isLoadingRequests: boolean
  onRefresh: () => void
  onClear: () => void
  onOpenDetail: (title: string, data: unknown, description?: string) => void
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Requests</h2>
          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
            {requestsTotal} request(s) registrada(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isLoadingRequests}
            className="gap-2"
          >
            <RefreshCcw className={cn('size-3.5', isLoadingRequests && 'animate-spin')} />
            Atualizar
          </Button>
          <Button variant="destructive" size="sm" onClick={onClear} className="gap-2">
            <Trash2 className="size-3.5" />
            Limpar
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-card/60 overflow-hidden">
        {!requests.length ? (
          <div className="px-5 py-8 text-center font-mono text-sm text-muted-foreground">
            {isLoadingRequests ? 'carregando...' : '— nenhuma request registrada —'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Path
                  </th>
                  <th className="px-4 py-3 text-left font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Horário
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {requests.map((request, index) => (
                  <tr
                    key={`${request.path}-${request.at}-${index}`}
                    className="border-b border-border/30 transition-colors hover:bg-secondary/20 last:border-0"
                  >
                    <td className="px-4 py-3">
                      <Badge variant={messageKindBadgeVariant(request.kind)}>{request.kind}</Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {request.path}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {formatTime(request.at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          onOpenDetail(
                            `Request ${request.id ?? index}`,
                            request.body,
                            `${request.kind} · ${request.path}`
                          )
                        }
                        className="h-7 gap-1.5 px-2.5 text-xs"
                      >
                        <Eye className="size-3.5" />
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
